import axios from "axios";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // No response received — network error or CORS block
    if (!error.response) {
      // Request was sent, user is online → likely a CORS block (browser hides the detail)
      const likelyCors = !!error.request && navigator.onLine;
      return Promise.reject({
        message: likelyCors
          ? "Request blocked by the server. Please contact an administrator."
          : "Unable to reach the server. Please check your internet connection.",
        status: -1,
        isNetworkError: true,
        isCorsLikely: likelyCors,
      });
    }

    const status = error.response.status;
    const data   = error.response.data ?? {};

    // 401 — pass through the original AxiosError so AuthGuard can attempt
    // a token refresh. If AuthGuard is not mounted (unauthenticated pages),
    // the error will propagate as-is to the calling code.
    if (status === 401) {
      return Promise.reject(error);
    }

    // 403 — forbidden
    if (status === 403) {
      return Promise.reject({
        message: "You do not have permission to perform this action.",
        status: -1,
        httpStatus: 403,
      });
    }

    // 404 — resource not found, never expose internal path
    if (status === 404) {
      return Promise.reject({
        message: "This resource is not available. Please contact an administrator if the problem persists.",
        status: -1,
        httpStatus: 404,
      });
    }

    // 500+ — never expose internal details to the user
    if (status >= 500) {
      return Promise.reject({
        message: "A server error occurred. Please try again or contact support.",
        status: -1,
        httpStatus: status,
      });
    }

    // 4xx (400, 409, 422...) — safe to forward backend message
    return Promise.reject({
      message: data.message || "An error occurred.",
      status: -1,
      httpStatus: status,
      data,
    });
  }
);

export default axiosInstance;
